/**
 * src/components/SharedUI.jsx — Reusable UI Primitives
 * AC Nautitech Design System
 */

import { useState, useEffect, useRef } from 'react';
import { X, ChevronLeft } from 'lucide-react';
import { avatarColor } from '../lib/mockData';
import useBreakpoint from '../hooks/useBreakpoint';

/* ─── Status Chip ─────────────────────────────────────── */
import { TASK_STATUS_COLORS, TASK_STATUS_STYLES } from '../lib/constants/statusColors';
import { normalizeTaskStatus, taskStatusLabel, isBlocked, isDone } from '../lib/constants/taskStatus';

const STATUS_MAP = {
  todo:        { c: 'var(--stodo)', b: 'var(--stodob)' },
  in_progress: { c: 'var(--spro)',  b: 'var(--sprob)' },
  blocked:     { c: 'var(--sblk)',  b: 'var(--sblkb)' },
  done:        { c: 'var(--sdn)',   b: 'var(--sdnb)' },
  // HEX_TODO[#F5F3FF] exception — see color-map.json
  // HEX_TODO[#7C3AED] exception — see color-map.json
  review:      { c: '#7C3AED',      b: '#F5F3FF' },
};

export function StatusChip({ status, onClick, onStatusSelect }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const slug = normalizeTaskStatus(status);
  const s = STATUS_MAP[slug] || STATUS_MAP.todo;
  const canInteract = !!(onClick || onStatusSelect);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const handleClick = (e) => {
    e.stopPropagation();
    if (onStatusSelect) { setOpen(v => !v); return; }
    if (onClick) onClick(e);
  };

  const selectStatus = (newStatus) => {
    setOpen(false);
    if (newStatus !== status) onStatusSelect(newStatus);
  };

  const statuses = Object.keys(STATUS_MAP);

  return (
    <span ref={ref} style={{ position: 'relative', display: 'inline-flex' }}>
      <span onClick={handleClick} style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '2px 10px', borderRadius: 9999, fontSize: 11, fontWeight: 500,
        background: s.b, color: s.c, cursor: canInteract ? 'pointer' : 'default',
        transition: 'transform 120ms var(--ease), box-shadow 120ms var(--ease)',
        whiteSpace: 'nowrap',
        boxShadow: open ? '0 0 0 2px ' + s.c + '40' : 'none',
      }}
      onMouseDown={e => canInteract && (e.currentTarget.style.transform = 'scale(0.95)')}
      onMouseUp={e => canInteract && (e.currentTarget.style.transform = 'scale(1)')}
      >
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.c }} />
        {taskStatusLabel(slug)}
        {onStatusSelect && <span style={{ fontSize: 8, marginLeft: 2, opacity: 0.6 }}>▾</span>}
      </span>

      {/* Floating popover */}
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: '50%', transform: 'translateX(-50%)',
          background: 'var(--card)', borderRadius: 14, padding: 6,
          boxShadow: '0 8px 32px rgba(0,0,0,0.18), 0 0 0 1px var(--bdr)',
          zIndex: 1100,
          display: 'flex', flexDirection: 'column', gap: 2, minWidth: 140,
          animation: 'statusPopIn 150ms cubic-bezier(0.34,1.56,0.64,1)',
        }}>
          {statuses.map(st => {
            const stc = STATUS_MAP[st] || STATUS_MAP.todo;
            const active = st === slug;
            return (
              <button key={st} onClick={(e) => { e.stopPropagation(); selectStatus(st); }} style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                borderRadius: 10, border: 'none', background: active ? stc.b : 'transparent',
                color: active ? stc.c : 'var(--t1)', fontSize: 13, fontWeight: active ? 600 : 400,
                cursor: 'pointer', transition: 'background 120ms, transform 80ms',
                textAlign: 'left', width: '100%',
              }}
              onMouseEnter={e => !active && (e.currentTarget.style.background = 'var(--stodob)')}
              onMouseLeave={e => !active && (e.currentTarget.style.background = 'transparent')}
              onMouseDown={e => e.currentTarget.style.transform = 'scale(0.97)'}
              onMouseUp={e => e.currentTarget.style.transform = 'scale(1)'}
              >
                <span style={{
                  width: 8, height: 8, borderRadius: '50%', background: stc.c,
                  boxShadow: active ? `0 0 0 3px ${stc.c}30` : 'none',
                  transition: 'box-shadow 150ms',
                }} />
                {taskStatusLabel(st)}
                {active && <span style={{ marginLeft: 'auto', fontSize: 11, opacity: 0.5 }}>✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </span>
  );
}

/* ─── Priority Badge ──────────────────────────────────── */
const PRIORITY_MAP = {
  P1: { c: 'var(--p1)', b: 'var(--p1b)' },
  P2: { c: 'var(--p2)', b: 'var(--p2b)' },
  P3: { c: 'var(--p3)', b: 'var(--p3b)' },
};

export function PriorityBadge({ priority }) {
  const p = PRIORITY_MAP[priority] || PRIORITY_MAP.P2;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: 11, fontWeight: 600, color: p.c,
    }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.c }} />
      {priority}
    </span>
  );
}

/* ─── Priority Bar (left edge of card) ────────────────── */
export function PriorityBar({ priority = 'P2', status }) {
  const norm = typeof status === 'string' ? status.toLowerCase().replace(/\s+/g, '_') : '';
  const color = isBlocked(norm) ? 'var(--sblk)' :
    isDone(norm) ? 'var(--sdn)' :
    PRIORITY_MAP[priority]?.c || 'var(--stodo)';
  return <div style={{ width: 3, borderRadius: '3px 0 0 3px', background: color, flexShrink: 0 }} />;
}

/* ─── Avatar ──────────────────────────────────────────── */
export function Avatar({ name, size = 36 }) {
  const initials = (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const bg = avatarColor(name);
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', background: bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: 'var(--card)', fontSize: size * 0.38, fontWeight: 600, flexShrink: 0,
      letterSpacing: -0.5,
    }}>
      {initials}
    </div>
  );
}

/* ─── Progress Bar ────────────────────────────────────── */
export function ProgressBar({ value = 0, height = 4, color = 'var(--accent)' }) {
  const [width, setWidth] = useState(0);
  useEffect(() => { const t = setTimeout(() => setWidth(value), 50); return () => clearTimeout(t); }, [value]);
  return (
    <div style={{
      width: '100%', height, borderRadius: height, background: 'var(--bdr)',
      overflow: 'hidden',
    }}>
      <div style={{
        height: '100%', borderRadius: height, background: color,
        width: `${width}%`, transition: 'width 600ms cubic-bezier(0.4,0,0.2,1)',
      }} />
    </div>
  );
}

/* ─── Stage Badge ─────────────────────────────────────── */
// Project execution stage colors (distinct from CRM pipeline stage colors in src/lib/constants/crm.js)
const PROJECT_STAGE_COLORS = {
  // English stages
  // #7C3AED is a categorical "Design" purple kept as exception (chart_colors bucket)
  // HEX_TODO[#7C3AED] exception — see color-map.json
  'Inquiry': 'var(--tm)', 'Survey / Scan': 'var(--info)', 'Design': '#7C3AED',
  'Approval': 'var(--warn)', 'Materials': 'var(--warn)', 'Production': 'var(--accent)',
  'Installation': 'var(--navy)', 'Complete': 'var(--ok)', 'Unknown': 'var(--tm)',
  // Spanish stages (from Airtable)
  'Escanear': 'var(--info)', 'Pedir materiales': 'var(--warn)',
  // HEX_TODO[#7C3AED] exception — see color-map.json
  'Diseño Detalles': '#7C3AED', 'Corte': 'var(--accent)',
  'Confeccion': 'var(--accent)', 'Confección': 'var(--accent)',
  'Control de calidad': 'var(--warn)', 'Instalacion y montaje': 'var(--navy)',
  'Instalación y montaje': 'var(--navy)',
  // Sub-stage prefixed variants (e.g. "2. Pedir materiales")
};

function resolveStageColor(stage) {
  if (!stage) return 'var(--tm)';
  const s = typeof stage === 'string' ? stage : String(stage);
  // Direct match
  if (PROJECT_STAGE_COLORS[s]) return PROJECT_STAGE_COLORS[s];
  // Strip numeric prefix (e.g. "2. Pedir materiales" → "Pedir materiales")
  const stripped = s.replace(/^\d+\.\s*/, '');
  if (PROJECT_STAGE_COLORS[stripped]) return PROJECT_STAGE_COLORS[stripped];
  // Fuzzy match on lowercase
  const lower = stripped.toLowerCase();
  for (const [key, color] of Object.entries(PROJECT_STAGE_COLORS)) {
    if (key.toLowerCase() === lower) return color;
  }
  return 'var(--tm)';
}

export function StageBadge({ stage, colorMap, solid }) {
  // If a custom colorMap is provided (e.g. CRM STAGE_COLORS), use it for solid badges
  const customColor = colorMap?.[stage];
  if (solid || customColor) {
    const bg = customColor || resolveStageColor(stage);
    return (
      <span style={{
        display: 'inline-block', padding: '2px 10px', borderRadius: 20,
        fontSize: 11, fontWeight: 700, color: 'var(--card)', background: bg, whiteSpace: 'nowrap',
      }}>
        {stage || 'Unknown'}
      </span>
    );
  }
  const color = resolveStageColor(stage);
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 8px', borderRadius: 9999, fontSize: 11, fontWeight: 500,
      background: `${color}18`, color, whiteSpace: 'nowrap',
    }}>
      {stage || 'Unknown'}
    </span>
  );
}

/* ─── Due Badge ───────────────────────────────────────── */
export function DueBadge({ dueDate }) {
  if (!dueDate) return null;
  const diff = Math.ceil((new Date(dueDate) - new Date()) / (1000 * 60 * 60 * 24));
  const isOverdue = diff < 0;
  const isUrgent = diff >= 0 && diff <= 2;
  const color = isOverdue ? 'var(--bad)' : isUrgent ? 'var(--warn)' : 'var(--tm)';
  const label = isOverdue ? `${Math.abs(diff)}d overdue` : diff === 0 ? 'Due today' : `${diff}d left`;
  return (
    <span style={{ fontSize: 11, fontWeight: 500, color }}>
      {label}
    </span>
  );
}

/* ─── Glass Card ──────────────────────────────────────── */
export function GlassCard({ children, style, onClick, padding = 16, ...rest }) {
  return (
    <div onClick={onClick} role="button" tabIndex={0} onKeyDown={(e) => { if (e.target !== e.currentTarget) return; if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(e); } }} {...rest} style={{
      background: 'var(--glass)',
      border: '1px solid var(--bdrg)',
      borderRadius: 14, padding,
      boxShadow: '0 1px 0 rgba(255,255,255,0.08) inset, var(--shc)',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'transform 120ms var(--ease), box-shadow 200ms var(--ease)',
      ...style,
    }}
    onMouseDown={e => onClick && (e.currentTarget.style.transform = 'scale(0.98)')}
    onMouseUp={e => onClick && (e.currentTarget.style.transform = 'scale(1)')}
    >
      {children}
    </div>
  );
}

/* ─── Hero Stat ───────────────────────────────────────── */
export function HeroStat({ value, label, sub, prefix = '', suffix = '' }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    const target = typeof value === 'number' ? value : parseFloat(value) || 0;
    let start = 0;
    const duration = 600;
    const startTime = Date.now();
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(start + (target - start) * eased * 10) / 10);
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [value]);

  // TICKET-752: a 6-figure value printed in full at 28px overflows the pill — the CEO's
  // Pipeline header read "€100646" and "€82175.6" clipped by the tile edges. Intl does
  // compact notation; money also has no business showing one decimal.
  const shown = typeof value === 'number'
    ? (Math.abs(value) >= 10000
      ? new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(display)
      : (Number.isInteger(value) ? Math.round(display) : display.toFixed(1)))
    : value;

  return (
    <GlassCard style={{ textAlign: 'center', flex: 1, minWidth: 0, background: 'var(--navy-surface)', border: 'none', overflow: 'hidden' }}>
      <div
        title={typeof value === 'number' ? `${prefix}${value}${suffix}` : undefined}
        style={{
          fontSize: 28, fontWeight: 800, color: 'var(--on-navy)', lineHeight: 1.1,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}
      >
        {prefix}{shown}{suffix}
      </div>
      <div style={{ fontSize: 13, fontWeight: 400, color: 'var(--on-navy)', opacity: 0.85, marginTop: 4 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--on-navy)', opacity: 0.65, marginTop: 2 }}>{sub}</div>}
    </GlassCard>
  );
}

/* ─── Modal Sheet (slide-up) ──────────────────────────── */
export function ModalSheet({ open, onClose, title, headerRight, children, id, zIndex = 1000 }) {
  const [visible, setVisible] = useState(false);
  const { isDesktop } = useBreakpoint();
  const titleId = id ? `modal-sheet-title-${id}` : undefined;

  useEffect(() => {
    if (open) setVisible(true);
    else {
      const t = setTimeout(() => setVisible(false), 300);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Lock body scroll when open (Bug 12 fix)
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  // Close on Escape -- unless a layer on top owns it (TICKET-1533: Esc in the quote
  // builder closed the builder AND this deal sheet, dropping the CEO on the pipeline).
  // ponytail: names the one known top layer; add a selector here if another full-screen
  // editor opens inside a ModalSheet.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (document.querySelector('[data-testid="quote-builder-screen"]')) return;
      onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!visible) return null;

  // Desktop: centered floating dialog. Mobile: bottom sheet with safe-area.
  const panelStyle = isDesktop ? {
    position: 'absolute', top: '50%', left: '50%',
    transform: open
      ? 'translate(-50%, -50%) scale(1)'
      : 'translate(-50%, -50%) scale(0.95)',
    opacity: open ? 1 : 0,
    maxWidth: 560, width: '90%',
    maxHeight: '85vh', background: 'var(--card)', borderRadius: 16,
    boxShadow: '0 24px 64px rgba(0,0,0,0.20), 0 0 0 1px var(--bdr)',
    overflow: 'auto',
    transition: 'transform 200ms cubic-bezier(0.34,1.56,0.64,1), opacity 200ms',
  } : {
    position: 'absolute', bottom: 0, left: 0, right: 0,
    maxWidth: 680, margin: '0 auto',
    maxHeight: '92vh', background: 'var(--card)', borderRadius: '20px 20px 0 0',
    boxShadow: 'var(--she)', overflow: 'auto',
    transform: open ? 'translateY(0)' : 'translateY(100%)',
    transition: 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1)',
    paddingBottom: 'env(safe-area-inset-bottom, 0px)',
  };

  return (
    <div
      // 2026-04-07: backdrop close handler ONLY responds to Escape. Do NOT
      // add Space or Enter — those bubble up from form inputs and get
      // preventDefault'd before the input can process them, breaking spacebar
      // entirely (regression caused by c92bbcc, fixed in 65f6b86's follow-up).
      role="button"
      tabIndex={0}
      aria-label="Close modal"
      onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); onClose?.(); } }}
      onClick={onClose}
      style={{
        // zIndex: a sheet opened from a full-screen layer (the quote builder sits at
        // 10000) passes 10010, the "above the builder" level the other sheets use.
        position: 'fixed', inset: 0, zIndex,
        background: open ? 'rgba(0,0,0,0.30)' : 'transparent',
        transition: 'background 200ms',
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        // stopPropagation only — never preventDefault on key events here. The
        // input has ALREADY received the event by the time this fires; we just
        // prevent it from reaching the backdrop's Escape handler with stale state.
        onKeyDown={(e) => e.stopPropagation()}
        style={panelStyle}
      >
        {/* Drag handle — mobile only */}
        {!isDesktop && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 4px' }}>
            <div style={{ width: 36, height: 4, borderRadius: 2, background: 'var(--tm)', opacity: 0.4 }} />
          </div>
        )}
        {/* Header — title may be a plain string (h3) or a node (e.g. an inline
            editable title). `headerRight` renders an accessory (e.g. a mic)
            between the title and the close button. */}
        {(title || headerRight) && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: isDesktop ? '16px 20px 12px' : '8px 16px 12px',
            borderBottom: '1px solid var(--bdr)',
          }}>
            {typeof title === 'string'
              ? <h3 id={titleId} style={{ fontSize: 17, fontWeight: 600, margin: 0, flex: 1, minWidth: 0 }}>{title}</h3>
              : <div id={titleId} style={{ flex: 1, minWidth: 0 }}>{title}</div>}
            {headerRight}
            <button onClick={onClose} style={{
              width: 32, height: 32, minWidth: 44, minHeight: 44, borderRadius: '50%', background: 'var(--bdr)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', border: 'none', flexShrink: 0,
            }} aria-label="Close">
              <X size={16} color="var(--t2)" strokeWidth={1.5} />
            </button>
          </div>
        )}
        <div style={{ padding: isDesktop ? 20 : 16 }}>{children}</div>
      </div>
    </div>
  );
}

/* ─── KPI Card ───────────────────────────────────────── */
export function KpiCard({ label, value, icon: Icon, color = 'var(--t1)', style }) {
  return (
    <GlassCard style={{ flex: '1 1 0', minWidth: 140, ...style }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 10,
          background: `color-mix(in srgb, ${color} 12%, transparent)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon size={18} strokeWidth={1.5} style={{ color }} />
        </div>
        <span style={{ fontSize: 13, color: 'var(--tm)', fontWeight: 500 }}>{label}</span>
      </div>
      <div style={{ fontSize: 28, fontWeight: 600, color }}>{value}</div>
    </GlassCard>
  );
}

/* ─── Empty State ─────────────────────────────────────── */
export function EmptyState({ icon: Icon, title, subtitle }) {
  return (
    <div style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--tm)' }}>
      {Icon && <Icon size={40} strokeWidth={1} style={{ marginBottom: 12, opacity: 0.5 }} />}
      <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--t2)', marginBottom: 4 }}>{title}</div>
      {subtitle && <div style={{ fontSize: 13 }}>{subtitle}</div>}
    </div>
  );
}

/* ─── Filter Chip ─────────────────────────────────────── */
export function FilterChip({ label, active, onClick }) {
  return (
    <button onClick={onClick} style={{
      padding: '6px 14px', borderRadius: 9999, fontSize: 12, fontWeight: 500,
      background: active ? 'var(--navy)' : 'var(--glass)',
      color: active ? 'var(--card)' : 'var(--t2)',
      border: active ? 'none' : '1px solid var(--bdr)',
      transition: 'all 150ms var(--ease)', whiteSpace: 'nowrap',
      minHeight: 44, flexShrink: 0,
    }}>
      {label}
    </button>
  );
}

/* ─── Search Bar ──────────────────────────────────────── */
export function SearchBar({ value, onChange, placeholder = 'Search...' }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      background: 'var(--glass)', borderRadius: 8, padding: '8px 12px',
      border: '1px solid var(--bdr)', minHeight: 44,
    }}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--tm)" strokeWidth="1.5" strokeLinecap="round">
        <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
      </svg>
      <input
        value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          border: 'none', background: 'transparent', outline: 'none', flex: 1, minWidth: 0,
          fontSize: 13, color: 'var(--t1)', minHeight: 44, borderRadius: 8,
        }}
      />
      {value && (
        <button onClick={() => onChange('')} style={{ background: 'none', padding: 0 }}>
          <X size={14} color="var(--tm)" strokeWidth={1.5} />
        </button>
      )}
    </div>
  );
}

/* ─── Skeleton Loading Primitives ───────────────────── */

const skeletonKeyframes = `@keyframes shimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}`;

const shimmerStyle = {
  background: 'linear-gradient(90deg, var(--bdr) 25%, var(--surface-line) 50%, var(--bdr) 75%)',
  backgroundSize: '200% 100%',
  animation: 'shimmer 1.5s ease-in-out infinite',
  borderRadius: 6,
};

export function SkeletonLine({ width = '100%', height = 14, style }) {
  return (
    <>
      <style>{skeletonKeyframes}</style>
      <div style={{ ...shimmerStyle, width, height, ...style }} />
    </>
  );
}

export function SkeletonCard({ lines = 3, style }) {
  return (
    <>
      <style>{skeletonKeyframes}</style>
      <div style={{
        padding: 16, borderRadius: 12, background: 'var(--card)',
        border: '1px solid var(--bdr)', ...style,
      }}>
        <SkeletonLine width="60%" height={16} style={{ marginBottom: 12 }} />
        {Array.from({ length: lines - 1 }).map((_, i) => (
          <SkeletonLine key={i} width={i === lines - 2 ? '40%' : '85%'} height={12}
            style={{ marginBottom: i < lines - 2 ? 8 : 0 }} />
        ))}
      </div>
    </>
  );
}

export function SkeletonDashboard() {
  return (
    <div style={{ padding: '24px 16px', maxWidth: 640, margin: '0 auto' }}>
      <style>{skeletonKeyframes}</style>
      {/* Gauge strip */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
        {[1, 2, 3, 4].map(i => (
          <div key={i} style={{ flex: 1, textAlign: 'center' }}>
            <SkeletonLine width={48} height={48} style={{ borderRadius: '50%', margin: '0 auto 8px' }} />
            <SkeletonLine width="70%" height={10} style={{ margin: '0 auto' }} />
          </div>
        ))}
      </div>
      {/* AI Focus cards */}
      <SkeletonLine width="40%" height={16} style={{ marginBottom: 12 }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {[1, 2, 3].map(i => (
          <SkeletonCard key={i} lines={2} style={{ padding: 12 }} />
        ))}
      </div>
    </div>
  );
}
